import styled from '@emotion/styled';
import type { MapType } from '@racemap/utilities/consts/events';
import { checkEnv } from '@racemap/utilities/functions/environmentals';
import { getStyleURL } from '@racemap/utilities/functions/map';
import { toArray } from '@racemap/utilities/functions/utils';
import { isDefined } from '@racemap/utilities/functions/validation';
import type {
  FeatureCollection,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson';
import { produce } from 'immer';
import type { MapEvent, MapStyleDataEvent, Map as MapboxMap, StyleSpecification } from 'mapbox-gl';
import { memo, useCallback, useMemo } from 'react';
import {
  type CSSProperties,
  Children,
  type FC,
  type ReactElement,
  cloneElement,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import MapGL, {
  type ErrorEvent,
  type MapMouseEvent,
  type MapRef,
  ScaleControl,
  type ViewState,
  type ViewStateChangeEvent,
} from 'react-map-gl/mapbox';
import { useDebounceCallback } from 'src/lib/customHooks/useDebounce';
import {
  type Bounds,
  fitViewStateToBounds,
  getBounds,
  getDefaultViewState,
} from 'src/lib/geoUtils';
import { useElementDimensions } from '../../../../lib/customHooks';
import { FitToViewportControl, MapboxStyleControl, NavigationControl } from '../Controls';
import { LockControl } from '../Controls/LockControl';
import { MapToolsControl } from '../Controls/MapToolsControl';
import type { MapLayerElement } from '../Layers';
import { AUTOFOCUS } from '../helpers';

const mapboxAccessToken = checkEnv(
  import.meta.env.VITE_FRONTEND_MAPBOX_ACCESS_TOKEN,
  'VITE_FRONTEND_MAPBOX_ACCESS_TOKEN',
);

export type Data = FeatureCollection<
  LineString | MultiLineString | Point | MultiPoint | Polygon | MultiPolygon
>;
export interface Props {
  id?: string;
  viewState?: ViewState;
  onDblClick?: (evt: MapMouseEvent) => void;
  defaultViewState?: ViewState;
  controls?: {
    navigation?: boolean;
    fitToViewport?: boolean;
    mapStyle?: boolean;
    scale?: boolean;
    lock?: boolean;
    help?: boolean;
  };
  children?: Array<MapLayerElement | Array<MapLayerElement>> | MapLayerElement;
  mapStyle?: MapType;
  style?: CSSProperties;
  onViewStateChange?: (viewState: ViewState) => void;
  onMapStyleChange?: (mapStyle: MapType) => void;
  scaleUnit?: 'imperial' | 'metric' | 'nautical';
  onMapLoad?: (map: MapboxMap) => void;
  onMapLockToggle?: (locked: boolean) => void;
  onFitToAll?: () => void;
  onFitToViewport?: () => void;
}

export interface NewDataProps {
  layer: string;
  data: Data | null;
  autofocus: boolean | AUTOFOCUS;
}

interface MapContextProps {
  map: MapRef | null;
  mapStyle: null | StyleSpecification;
  setMouseEnterAction: (sourceId: string, action: (evt: MapMouseEvent) => void) => void;
  setMouseLeaveAction: (sourceId: string, action: (evt: MapMouseEvent) => void) => void;
  setOnClickAction: (sourceId: string, action: (evt: MapMouseEvent) => void) => void;
  addInteractiveLayerIds: (layerId: string) => void;
}

const defaultMapContext = {
  map: null,
  mapStyle: null,
  setMouseEnterAction: () => {},
  setMouseLeaveAction: () => {},
  setOnClickAction: () => {},
  addInteractiveLayerIds: () => {},
};

export const MapContext = createContext<MapContextProps>(defaultMapContext);

export const BasicMap: FC<Props> = memo(
  ({
    id,
    viewState,
    onDblClick,
    children,
    controls,
    style,
    mapStyle = null,
    defaultViewState,
    onViewStateChange,
    onMapStyleChange,
    onMapLoad,
    scaleUnit = 'metric',
    onMapLockToggle,
    onFitToAll,
    onFitToViewport,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapRef>(null);
    const { width, height, visible } = useElementDimensions(containerRef);
    const [currentBounds, setCurrentBounds] = useState<Bounds | null>(null);
    const [currentViewState, setCurrentViewState] = useState<ViewState>(
      () => defaultViewState || getDefaultViewState(),
    );
    const [isMouseInMap, setIsMouseInMap] = useState(false);
    const [mouseEnterActions, setMouseEnterActions] = useState<
      Map<string, (evt: MapMouseEvent) => void>
    >(new Map());
    const [mouseLeaveActions, setMouseLeaveActions] = useState<
      Map<string, (evt: MapMouseEvent) => void>
    >(new Map());
    const [onClickActions, setOnClickActions] = useState<Map<string, (evt: MapMouseEvent) => void>>(
      new Map(),
    );
    const [currentHoveredElements, setCurrentHoveredElements] = useState<Set<string>>(new Set());
    const [interactiveLayerIds, setInteractiveLayerIds] = useState<Set<string>>(new Set());
    const [internalMapStyle, setInternalMapStyle] = useState<null | StyleSpecification>(null);
    const setInternalMapStyleDebounced = useDebounceCallback(setInternalMapStyle, 1000);
    const focusedOnceLayersRef = useRef<Set<string>>(new Set());
    // The bounds an autofocus has already framed. Without it, every transition of `visible`
    // refits them — and a panel that briefly measures zero is enough to throw away the
    // viewport the operator chose.
    const fittedBoundsRef = useRef<Bounds | null>(null);
    // Someone who has moved the map has framed it themselves. Data that only arrives
    // afterwards must not take that frame away again.
    const userAdjustedRef = useRef(false);

    useEffect(() => {
      if (viewState == null) return;
      setCurrentViewState(viewState);
    }, [viewState]);

    useEffect(() => {
      if (visible && mapRef.current != null) {
        mapRef.current.getMap().resize();
      }
    }, [visible, width, height]);

    useEffect(() => {
      if (currentBounds == null || !visible) return;
      if (fittedBoundsRef.current === currentBounds) return;
      fittedBoundsRef.current = currentBounds;
      fitFeatures();
    }, [visible, currentBounds]);

    const fitFeatures = useCallback(() => {
      if (currentBounds != null && visible) {
        handleViewStateChange(
          fitViewStateToBounds({
            baseViewState: currentViewState,
            bounds: currentBounds,
            viewportWidth: width,
            viewportHeight: height,
          }),
        );
      }
    }, [currentBounds, width, height, setCurrentViewState, visible]);

    const handleViewStateChange = (viewState: ViewState) => {
      if (onViewStateChange != null) {
        onViewStateChange(viewState);
      } else {
        setCurrentViewState(viewState);
      }
    };

    const handleNewData = ({ layer, data, autofocus = false }: NewDataProps) => {
      const once = autofocus === AUTOFOCUS.ONCE;

      const shouldFocus =
        autofocus === true ||
        autofocus === AUTOFOCUS.EVERYTIME ||
        (once && !focusedOnceLayersRef.current.has(layer) && !userAdjustedRef.current);

      if (!shouldFocus || data == null || data.features.length === 0) return;
      if (!once && isMouseInMap) return;

      const bounds = getBounds(data.features);
      if (bounds == null) return;

      if (once && visible) focusedOnceLayersRef.current.add(layer);

      // bail on identical bounds
      setCurrentBounds((prev) => (prev != null && boundsAreEqual(prev, bounds) ? prev : bounds));
    };

    const handleMouseMove = (evt: MapMouseEvent) => {
      if (evt.features != null) {
        const features: Array<{ source?: string }> = evt.features;
        const hoveredSourceIds = new Set(features.map((f) => f.source).filter(isDefined));

        const isOverClickable = [...hoveredSourceIds].some((id) => onClickActions.has(id));
        evt.target.getCanvas().style.cursor = isOverClickable ? 'pointer' : '';

        for (const sourceId of hoveredSourceIds) {
          const enterAction = mouseEnterActions.get(sourceId);

          if (enterAction != null && !currentHoveredElements.has(sourceId)) {
            enterAction(evt);
            setCurrentHoveredElements((prevElements) =>
              produce(prevElements, (draft) => {
                draft.add(sourceId);
              }),
            );
          }
        }

        if (currentHoveredElements.size > 0) {
          for (const sourceId of currentHoveredElements) {
            if (hoveredSourceIds.has(sourceId)) return;
            const leaveAction = mouseLeaveActions.get(sourceId);

            if (leaveAction != null) {
              leaveAction(evt);
              setCurrentHoveredElements((prevElements) =>
                produce(prevElements, (draft) => {
                  draft.delete(sourceId);
                }),
              );
            }
          }
        }
      }
    };

    const handleClick = (evt: MapMouseEvent) => {
      if (evt.features != null) {
        const features: Array<{ source?: string }> = evt.features;
        const sourceIds = new Set(features.map((f) => f.source).filter(isDefined));
        for (const sourceId of sourceIds) {
          if (evt.defaultPrevented) return;
          onClickActions.get(sourceId)?.(evt);
        }
      }
    };

    const setMouseEnterAction = useCallback(
      (sourceId: string, action: (evt: MapMouseEvent) => void) => {
        setMouseEnterActions((prevActions) =>
          produce(prevActions, (draft) => {
            draft.set(sourceId, action);
          }),
        );
      },
      [setMouseEnterActions],
    );

    const setMouseLeaveAction = useCallback(
      (sourceId: string, action: (evt: MapMouseEvent) => void) => {
        setMouseLeaveActions((prevActions) =>
          produce(prevActions, (draft) => {
            draft.set(sourceId, action);
          }),
        );
      },
      [setMouseLeaveActions],
    );

    const setOnClickAction = useCallback(
      (sourceId: string, action: (evt: MapMouseEvent) => void) => {
        setOnClickActions((prevActions) =>
          produce(prevActions, (draft) => {
            draft.set(sourceId, action);
          }),
        );
      },
      [setOnClickActions],
    );

    const addInteractiveLayerIds = useCallback(
      (layerId: string) => {
        setInteractiveLayerIds((prevIds) =>
          produce(prevIds, (draft) => {
            draft.add(layerId);
          }),
        );
      },
      [setInteractiveLayerIds],
    );

    const prepareChildren = (child: ReactElement): ReactElement | undefined => {
      if (child != null) {
        const newProps = { ...child.props };
        newProps.onNewData = (props: NewDataProps) => {
          if (child.props.onNewData != null) child.props.onNewData(props);
          handleNewData(props);
        };

        return cloneElement(child, newProps);
      }
    };

    const handleError = (error: ErrorEvent) => {
      if (
        /Source "mapbox-gl-draw-.*" cannot be removed while layer "stroke-.*-point-on-linestring-.*" is using it./.test(
          error.error.message,
        )
      ) {
        // ignore that error. happens, when draw control and point on linestrings layer combined and draw control get removed
        console.debug(error.error.message);
        return;
      }
      if (/Layer with id .* does not exist on this map./.test(error.error.message)) {
        // ignore that error. it happens when focus point layer is generated in without drawing context
        console.debug(
          `beforeId is used without to check, that layer exist. use style in context to add only the id of existing layers. Raw Error: ${error.error.message}`,
        );
        return;
      }

      console.error(error);
    };

    const context = useMemo(
      () => ({
        setMouseEnterAction,
        setMouseLeaveAction,
        addInteractiveLayerIds,
        setOnClickAction,
        mapStyle: internalMapStyle,
        map: mapRef.current,
      }),
      [
        setMouseEnterAction,
        setMouseLeaveAction,
        addInteractiveLayerIds,
        setOnClickAction,
        internalMapStyle,
      ],
    );

    return (
      <Container
        onMouseEnter={() => setIsMouseInMap(true)}
        onMouseLeave={() => setIsMouseInMap(false)}
        style={style}
        ref={containerRef}
      >
        <MapContext.Provider value={context}>
          <MapGL
            id={id}
            {...currentViewState}
            mapStyle={getStyleURL(mapStyle)}
            style={{ width, height }}
            onMove={(evt: ViewStateChangeEvent) => {
              if (isUserGesture(evt)) userAdjustedRef.current = true;
              handleViewStateChange(evt.viewState);
            }}
            mapboxAccessToken={mapboxAccessToken}
            onDblClick={onDblClick}
            onMouseMove={handleMouseMove}
            onMouseUp={handleClick}
            interactiveLayerIds={Array.from(interactiveLayerIds)}
            ref={mapRef}
            onError={(error: ErrorEvent) => handleError(error)}
            onStyleData={(evt: MapStyleDataEvent) =>
              setInternalMapStyleDebounced(
                (evt as MapStyleDataEvent & { target: MapboxMap }).target.getStyle(),
              )
            }
            onLoad={(e: MapEvent) => {
              onMapLoad?.(e.target);
            }}
          >
            {controls?.mapStyle && (
              <MapboxStyleControl value={mapStyle} onChange={onMapStyleChange} />
            )}
            {Children.map(toArray(children).flat(), prepareChildren)}
            {controls?.scale && (
              <ScaleControl unit={scaleUnit} position="top-right" style={{ height: 22 }} />
            )}
            {controls?.navigation && <NavigationControl />}
            {controls?.fitToViewport && (
              <FitToViewportControl
                onFitRequest={() => {
                  onFitToViewport?.();
                  fitFeatures();
                }}
              />
            )}
            {controls?.lock && <LockControl onMapLockToggle={onMapLockToggle} />}
            {controls?.help && <MapToolsControl onFitToAll={onFitToAll} />}
          </MapGL>
        </MapContext.Provider>
      </Container>
    );
  },
);

/**
 * Props-driven camera changes never reach the camera callbacks, so a move that carries the DOM
 * event that caused it is the operator dragging, wheeling or hitting a zoom button.
 * `ViewStateChangeEvent` is a union over every camera event and only some members declare
 * `originalEvent`, hence the cast.
 */
const isUserGesture = (evt: ViewStateChangeEvent): boolean =>
  (evt as { originalEvent?: unknown }).originalEvent != null;

const boundsAreEqual = (a: Bounds, b: Bounds): boolean =>
  a.minLat === b.minLat && a.maxLat === b.maxLat && a.minLng === b.minLng && a.maxLng === b.maxLng;

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;

  .mapboxgl-ctrl-top-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .mapboxgl-ctrl-group:has(.mapboxgl-ctrl-zoom-out) {
      max-width: 30px;
    }
  }

  .mapboxgl-ctrl-top-left {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    max-width: calc(100% - 150px);
  }

  .mapboxgl-ctrl-group-vertical {
    display: flex;
    flex-direction: row;
  }

  .mapbox-ctrl-button-style {
    display: inline-block;
    width: unset;
    padding-left: 7px;
    padding-right: 7px;
    font-weight: bold;
    border-top: unset;
    border-bottom: unset;
  }

  .mapbox-ctrl-button-style-choosed {
    background-color: #ddd;
  }

  .mapbox-ctrl-button-style:first-of-type {
    border-radius: 4px 0 0 4px;
  }

  .mapbox-ctrl-button-style:last-of-type {
    border-radius: 0 4px 4px 0;
  }

  .mapboxgl-ctrl-group-vertical > button + button {
    border-left: 1px solid #ddd;
  }
`;
